import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { GenericFormPage } from '../../components/generic';
import { usePayable, Payable } from './useData';
import { moduleConfig, getInitialFormData, mapItemToFormData } from './config';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';

interface InvoiceExtractMeta {
  confidence?: 'high' | 'medium' | 'low';
  warnings?: string[];
  possibleDuplicates?: Array<{
    id: number;
    payeeName: string;
    amount: number;
    dueDate: string | null;
    status: string;
  }>;
  extractionMethod?: 'ai' | 'heuristic';
  invoiceNumber?: string | null;
}

const Form: React.FC = () => {
  const location = useLocation();
  const { colors } = useTheme();
  const meta = (location.state as { invoiceExtractMeta?: InvoiceExtractMeta } | null)?.invoiceExtractMeta;
  const showBanner = Boolean(meta);

  return (
    <div className="flex flex-col h-full min-h-0">
      {showBanner && (
        <div className={cn('flex-shrink-0 mx-4 mt-3 rounded-lg border px-4 py-3', colors.border, colors.surface)}>
          <p className={cn('text-sm font-medium', colors.text)}>
            Invoice fields pre-filled
            {meta?.extractionMethod === 'ai' ? ' from AI' : ' from document text'}
            {meta?.confidence ? ` · confidence: ${meta.confidence}` : ''}
            {meta?.invoiceNumber ? ` · #${meta.invoiceNumber}` : ''}
          </p>
          <p className={cn('text-xs mt-1', colors.textMuted)}>
            Review the values below, fix anything wrong, then save. The uploaded file is already attached.
          </p>
          {meta?.warnings && meta.warnings.length > 0 && (
            <ul className={cn('mt-2 text-xs list-disc pl-4 space-y-0.5', colors.textSecondary)}>
              {meta.warnings.map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          )}
          {meta?.possibleDuplicates && meta.possibleDuplicates.length > 0 && (
            <div className="mt-2 text-xs">
              <p className={cn('font-medium', colors.text)}>Possible duplicates</p>
              <ul className="mt-1 space-y-1">
                {meta.possibleDuplicates.map((d) => (
                  <li key={d.id}>
                    <Link className="text-indigo-600 hover:underline" to={`/payables/${d.id}`}>
                      #{d.id} {d.payeeName}
                    </Link>
                    {' — '}
                    {d.amount.toFixed(2)} · {d.status}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
      <div className="flex-1 min-h-0">
        <GenericFormPage<Payable>
          useData={usePayable}
          config={{
            ...moduleConfig,
            getInitialFormData: getInitialFormData as () => Partial<Payable>,
            mapItemToFormData: mapItemToFormData as (item: Payable) => Partial<Payable>,
          }}
        />
      </div>
    </div>
  );
};

export default Form;
